import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";

const SubAttendance = () => {
  const location = useLocation();

  const [attendance, setAttendance] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [selectedOutlet, setSelectedOutlet] = useState("all");
  const [search, setSearch] = useState("");
  const [routeFilter, setRouteFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllMode, setShowAllMode] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get("filter");
    const date = params.get("date");

    if (filter) setRouteFilter(filter.toLowerCase());

    if (date) {
      setSelectedDate(date);
      fetchAttendance(date); // fetch the URL date!
    } else {
      const today = new Date().toISOString().split("T")[0];
      setSelectedDate(today);
      fetchAttendance(today);
    }
  }, [location.search]);

  // Fetch attendance (normal single-day)
  const fetchAttendance = async (date) => {
    if (!date) return;

    setLoading(true);
    try {
      const res = await api.get("/hrms/attendance", {
        params: {
          type: "daily",
          date,
          limit: 10000,
          page: 1,
        },
      });
      setAttendance(res.data.data || []);
    } catch {
      Swal.fire("Error", "Failed to fetch attendance", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL attendance
  const fetchAllAttendance = async () => {
    setShowAllMode(true);
    setRouteFilter(null);
    setLoading(true);

    try {
      const res = await api.get("/hrms/attendance", {
        params: {
          type: "all",
          limit: 50000,
          page: 1,
        },
      });

      setAttendance(res.data.data || []);
    } catch {
      Swal.fire("Error", "Failed to fetch all attendance", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showAllMode && selectedDate) {
      fetchAttendance(selectedDate);
    }
  }, [selectedDate]);

  const departmentList = [
    "all",
    ...new Set(attendance.map((a) => a.department).filter(Boolean)),
  ];

  const outletList = [
    "all",
    ...new Set(attendance.map((a) => a.outlet).filter(Boolean)),
  ];

  const formatTime = (t) => (t && t !== "-" ? t : "-");

  const filteredAttendance = attendance.filter((a) => {
    if (!showAllMode) {
      if (routeFilter === "present")
        return a.status.toLowerCase() === "present";
      if (routeFilter === "absent") return a.status.toLowerCase() === "absent";
    }

    if (selectedDept !== "all" && a.department !== selectedDept) return false;

    if (selectedOutlet !== "all" && a.outlet !== selectedOutlet) return false;

    const s = search.toLowerCase();
    if (
      s &&
      !a.name.toLowerCase().includes(s) &&
      !a.employee_code.toLowerCase().includes(s)
    ) {
      return false;
    }

    return true;
  });

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="hrms-report-attendance-cont">
            <h5 className="fw-semibold fs-4">Attendance Records</h5>
            <div className="row row-gap-3 mt-3">
              <div className="col-lg-2 col-md-6 col-sm-12">
                <input
                  type="date"
                  className="form-control"
                  value={selectedDate}
                  onChange={(e) => {
                    setShowAllMode(false);
                    setSelectedDate(e.target.value);
                  }}
                />
              </div>

              {/* Department */}
              <div className="col-lg-3 col-md-6 col-sm-12">
                <select
                  className="form-select"
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                >
                  {departmentList.map((dept, idx) => (
                    <option key={idx} value={dept}>
                      {dept === "all" ? "All Departments" : dept}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-12">
                <select
                  className="form-select"
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                >
                  {outletList.map((o, idx) => (
                    <option key={idx} value={o}>
                      {o === "all" ? "All Outlet" : o}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div className="col-lg-3 col-md-6 col-sm-12">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Employee..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Show All Button */}
              <div className="col-lg-2 col-md-6 col-sm-12">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={fetchAllAttendance}
                >
                  Show All
                </button>
              </div>
            </div>

            {/* Loading */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="hrms-report-attendance-cont-table mt-4">
                  <div className="table-responsive">
                    <table className="table table-hover m-0">
                      <thead className="border-top">
                        <tr>
                          <th>Employee Code</th>
                          <th>Employee</th>
                          <th>Department</th>
                          <th>Outlet</th>
                          <th>Shift (Start/End)</th>
                          <th>Times (Entry/Exit)</th>
                          <th>Worked</th>
                          <th>Break Timing</th>
                          <th>Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredAttendance.length > 0 ? (
                          filteredAttendance.map((a) => (
                            <tr key={a.employee_code}>
                              <td>{a.employee_code}</td>
                              <td>{a.name}</td>
                              <td>{a.department}</td>
                              <td>{a.outlet}</td>
                              <td>
                                {formatTime(a.shift_in) || "-"} /{" "}
                                {formatTime(a.shift_out) || "-"}
                              </td>
                              <td>
                                {formatTime(a.in_time)} /{" "}
                                {formatTime(a.out_time)}
                              </td>
                              <td>{a.worked_minutes || "-"}</td>
                              {/* <td>
                                {Object.keys(a.breaks).length === 0
                                  ? "No Break"
                                  : Object.entries(a.breaks).map(
                                      ([name, time]) => (
                                        <div key={name}>
                                          <strong>{name}</strong>: {time}
                                        </div>
                                      )
                                    )}
                              </td> */}
                              <td>
                                {Object.entries(a.breaks).filter(
                                  ([_, v]) => v !== "0m"
                                ).length === 0 ? ( // hide zero-minute breaks
                                  <span
                                    style={{
                                      color: "#6c757d",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    No Breaks
                                  </span>
                                ) : (
                                  <div style={{ lineHeight: "1.4" }}>
                                    {Object.entries(a.breaks)
                                      .filter(([_, v]) => v !== "0m")
                                      .map(([name, time]) => (
                                        <div key={name}>
                                          <span style={{ fontWeight: 600 }}>
                                            {name}:
                                          </span>
                                          {"-"}
                                          <span>{time}</span>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </td>

                              <td>
                                <span
                                  className={`badge rounded-pill px-3 py-2 ${
                                    a.status === "Absent"
                                      ? "bg-danger-subtle text-danger fw-semibold"
                                      : a.status === "Present"
                                      ? "bg-success-subtle text-success fw-semibold"
                                      : a.status.toLowerCase().includes("late")
                                      ? "bg-warning-subtle text-dark fw-semibold"
                                      : "bg-secondary-subtle text-muted fw-semibold"
                                  }`}
                                >
                                  {a.status}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No attendance found for selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubAttendance;
